import { useEffect, useState } from 'react'
import {
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { CreditCardOutlined } from '@ant-design/icons'
import useAuth from 'hooks/useAuth'
import { postWithToken } from 'hooks/http'
import { usePlatform } from 'hooks/usePlatform'
import { useOrganizationStatus } from 'hooks/useOrganizationStatus'
import { analytics } from 'utils/analytics'
import { PRODUCTS, getSizeTierForCount, getProductPrice, mapTierToProduct } from '../SelectSubscription/pricingData'
import type { ProductName } from '../SelectSubscription/pricingData'
import EmployeeSlider from '../SelectSubscription/EmployeeSlider'
import ProductCard from '../SelectSubscription/ProductCard'

// ================================|| MANAGE SUBSCRIPTION ||================================ //

function ManageSubscription() {
  const { token } = useAuth()
  const { updateSubscriptionPlan, createAccountCheckoutSession, cancelSubscription } = usePlatform(token || '')
  const { organizationStatus, isLoadingOrganizationStatus, handleRefetch } = useOrganizationStatus(token || '', null)

  const [employeeCount, setEmployeeCount] = useState(10)
  const [loadingProduct, setLoadingProduct] = useState<ProductName | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelResult, setCancelResult] = useState<{ periodEnd: string } | null>(null)
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null)

  // Derive current subscription product + size
  const currentSubscription = organizationStatus?.subscriptionInformation?.subscriptions?.find((sub) =>
    ['active', 'trialing', 'past_due'].includes(sub.status)
  )
  const currentTier = organizationStatus?.subscriptionInformation?.subscriptionTiers?.find(
    (tier) => tier.id === currentSubscription?.tierId
  )
  const currentMapping = currentTier
    ? mapTierToProduct(currentTier.label, currentTier.name, currentTier.maxUsers)
    : null
  const isTrialing = currentSubscription?.status === 'trialing'

  // Initialize employee count from current tier's maxUsers
  useEffect(() => {
    if (currentTier?.maxUsers) {
      setEmployeeCount(currentTier.maxUsers)
    }
  }, [currentTier?.maxUsers])

  useEffect(() => {
    analytics.track('manage_subscription_started')
  }, [])

  const sizeTier = getSizeTierForCount(employeeCount)

  const getCtaLabel = (productName: ProductName): string => {
    if (!currentSubscription) return 'Subscribe'

    if (currentMapping && currentMapping.product === productName && currentMapping.sizeTier === sizeTier) {
      return 'Current Plan'
    }

    const currentPrice = currentMapping ? getProductPrice(currentMapping.product, currentMapping.sizeTier) : null
    const newPrice = getProductPrice(productName, sizeTier)

    if (currentPrice === null || newPrice === null) return 'Select Plan'
    if (newPrice > currentPrice) return 'Upgrade'
    if (newPrice < currentPrice) return 'Downgrade'
    return 'Switch Plan'
  }

  const isCurrent = (productName: ProductName): boolean => {
    return !!(currentMapping && currentMapping.product === productName && currentMapping.sizeTier === sizeTier)
  }

  const handleSelect = async (product: ProductName) => {
    setLoadingProduct(product)
    setUpdateSuccess(null)

    try {
      if (currentSubscription) {
        // Existing subscription — update in place (proration applied by Stripe)
        await updateSubscriptionPlan(product, sizeTier)

        const productDisplay = PRODUCTS.find((p) => p.name === product)?.displayName || product
        analytics.track('subscription_plan_updated', {
          product,
          sizeTier,
          employeeCount,
          fromProduct: currentMapping?.product,
          fromSizeTier: currentMapping?.sizeTier,
        })

        setUpdateSuccess(
          `Your subscription has been updated to ${productDisplay}. Proration has been applied automatically.`
        )
        handleRefetch()
      } else {
        // No active subscription — go through checkout (no trial, collects payment)
        const result = await createAccountCheckoutSession(product, sizeTier)

        analytics.track('subscription_checkout_started', {
          product,
          sizeTier,
          employeeCount,
        })

        window.location.href = result.checkoutUrl
      }
    } catch (error) {
      console.error('Failed to update subscription:', error)
    } finally {
      setLoadingProduct(null)
    }
  }

  const handleContactUs = () => {
    const subject = encodeURIComponent('Purcent Enterprise Inquiry')
    const body = encodeURIComponent(
      `Hi Purcent team,\n\nI'm interested in learning more about your enterprise plans.\n\nCompany size: ${employeeCount >= 300 ? '300+' : employeeCount} employees\n\nPlease get in touch at your earliest convenience.\n\nThank you`
    )
    window.location.href = `mailto:sales@purcent.com?subject=${subject}&body=${body}`
  }

  const handleUpdatePaymentMethod = () => {
    if (!token) return
    postWithToken('account/session', token, { return_url: '/manage-subscription' })
      .then((response: { url?: string }) => {
        if (response.url) {
          window.location.href = response.url
        }
      })
      .catch((err: unknown) => {
        console.error('Failed to open billing portal:', err)
      })
  }

  const handleCancelClick = () => {
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    setIsCancelling(true)
    try {
      const result = await cancelSubscription()
      analytics.track('subscription_cancelled', {
        product: currentMapping?.product,
        sizeTier: currentMapping?.sizeTier,
        periodEnd: result.periodEnd,
      })
      setCancelResult({ periodEnd: result.periodEnd })
      setCancelDialogOpen(false)
      handleRefetch()
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
    } finally {
      setIsCancelling(false)
    }
  }

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (isLoadingOrganizationStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress color="primary" />
      </Box>
    )
  }

  return (
    <Grid
      container
      spacing={3}
      sx={{ minHeight: '100vh', paddingTop: { xs: '2vh', md: '3vh' }, px: { xs: 2, sm: 3, md: 4 } }}
      justifyContent="center"
    >
      <Grid size={12}>
        <Paper
          variant="outlined"
          elevation={0}
          sx={(theme) => ({
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
            mx: 'auto',
            maxWidth: 1200,
            bgcolor: theme.palette.mode === 'dark' ? '#000' : 'background.paper',
          })}
        >
          <Stack spacing={3}>
            <Stack spacing={1} sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' } }}>
                Manage Your Subscription
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {currentMapping
                  ? `You are currently on the ${PRODUCTS.find((p) => p.name === currentMapping.product)?.displayName} plan.`
                  : 'You do not have an active subscription. Select a plan to get started.'}{' '}
                All subscriptions are billed annually.
              </Typography>
            </Stack>

            {updateSuccess && (
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                <Typography variant="body1" sx={{ color: 'success.dark', fontWeight: 600 }}>
                  {updateSuccess}
                </Typography>
              </Box>
            )}

            {cancelResult && (
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                <Typography variant="body1" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                  Your subscription has been cancelled and will end on {formatDate(cancelResult.periodEnd)}.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <EmployeeSlider value={employeeCount} onChange={setEmployeeCount} />
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
              {PRODUCTS.map((product) => (
                <Grid key={product.name} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <ProductCard
                    product={product}
                    sizeTier={sizeTier}
                    isCurrent={isCurrent(product.name)}
                    ctaLabel={getCtaLabel(product.name)}
                    showTrial={false}
                    loading={loadingProduct === product.name}
                    onSelect={handleSelect}
                    onContactUs={handleContactUs}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 1 }}>
              * Smart Imports for Purcent Reward and Purcent Complete is limited to Total Reward Statement data imports
              only.
            </Typography>

            {currentSubscription && (
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CreditCardOutlined />}
                  onClick={handleUpdatePaymentMethod}
                  sx={{ textTransform: 'none', borderColor: '#087575', color: '#087575' }}
                >
                  Update Payment Method
                </Button>
                <Button variant="text" color="error" onClick={handleCancelClick} sx={{ textTransform: 'none' }}>
                  Cancel Subscription
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Grid>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription?
            {isTrialing ? (
              <>
                {' '}
                Your subscription will end at the end of your trial period. You will lose access to all features after
                that date.
              </>
            ) : (
              <>
                {' '}
                Your subscription will remain active until the end of your current billing period. You will not be
                charged again after that date.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isCancelling}>
            Keep Subscription
          </Button>
          <Button onClick={handleCancelConfirm} color="error" disabled={isCancelling} variant="contained">
            {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

export default ManageSubscription
